import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { Plus, MagnifyingGlass, Folder, Lightning, Drop, Funnel, Archive, ArrowCounterClockwise } from '@phosphor-icons/react';

const SERVICE_TYPES = ['Water Query', 'Electricity Query', 'New Connection', 'New Account Opening', 'Payment Arrangement',
                       'Building Plans', 'Tariff Change Application', 'Billing Query', 'Value Dispute', 'Refund Request', 'Energy Audit'];

const INTERNAL_STATUSES = ['New Service Request', 'Quotation Accepted', 'Site Visit Scheduled',
                           'Assigned to Agent', 'Query Logged', 'Under Review', 'Approved', 'Completed'];

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

const STATUS_COLORS = {
  'New Service Request': 'bg-blue-100 text-blue-700',
  'Quotation Accepted': 'bg-indigo-100 text-indigo-700',
  'Site Visit Scheduled': 'bg-cyan-100 text-cyan-700',
  'Assigned to Agent': 'bg-gray-100 text-gray-700',
  'Query Logged': 'bg-yellow-100 text-yellow-700',
  'Under Review': 'bg-orange-100 text-orange-700',
  'Approved': 'bg-green-100 text-green-700',
  'Completed': 'bg-emerald-100 text-emerald-700'
};

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-700',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-orange-100 text-orange-700',
  'Urgent': 'bg-red-100 text-red-700'
};

const AdminCases = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [newCase, setNewCase] = useState({
    company_id: '',
    service_type: '',
    internal_status: 'New Service Request',
    priority: 'Medium',
    internal_notes: ''
  });

  const filters = {
    service_type: searchParams.get('service_type') || '',
    internal_status: searchParams.get('internal_status') || '',
    priority: searchParams.get('priority') || ''
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = new URLSearchParams();
        if (filters.service_type) params.set('service_type', filters.service_type);
        if (filters.internal_status) params.set('internal_status', filters.internal_status);
        if (filters.priority) params.set('priority', filters.priority);
        if (showArchived) params.set('archived_only', 'true');

        const [casesRes, companiesRes] = await Promise.all([
          api.get(`/cases?${params}`),
          api.get('/companies')
        ]);
        setCases(casesRes.data);
        setCompanies(companiesRes.data);
      } catch (error) {
        console.error('Fetch error:', error);
        toast.error('Failed to load cases');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters.service_type, filters.internal_status, filters.priority, showArchived]);

  const refresh = async () => {
    const params = new URLSearchParams();
    if (filters.service_type) params.set('service_type', filters.service_type);
    if (filters.internal_status) params.set('internal_status', filters.internal_status);
    if (filters.priority) params.set('priority', filters.priority);
    if (showArchived) params.set('archived_only', 'true');
    const { data } = await api.get(`/cases?${params}`);
    setCases(data);
  };

  const handleArchive = async (c) => {
    if (!window.confirm(`Archive this query (${c.service_type})?\n\nIt will be hidden from the case list and the client portal. Quotations and invoices are retained for 5 years and the query can be restored at any time.`)) return;
    setBusyId(c.case_id);
    try {
      await api.post(`/cases/${c.case_id}/archive`, { reason: 'Archived from case list' });
      toast.success('Query archived');
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (c) => {
    setBusyId(c.case_id);
    try {
      await api.post(`/cases/${c.case_id}/restore`);
      toast.success('Query restored');
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    try {
      await api.post('/cases', newCase);
      toast.success('Case created successfully');
      setIsDialogOpen(false);
      setNewCase({
        company_id: '',
        service_type: '',
        internal_status: 'New Service Request',
        priority: 'Medium',
        internal_notes: ''
      });
      // Refresh
      const { data } = await api.get('/cases');
      setCases(data);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const query = search.trim().toLowerCase();
  const visibleCases = query
    ? cases.filter((c) =>
        [c.case_id, c.service_type, c.company_name, c.internal_status, c.priority, c.internal_notes]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(query))
      )
    : cases;

  return (
    <AdminLayout title="Cases">
      <div className="space-y-6 fade-in">
        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative w-full sm:w-80">
                <MagnifyingGlass className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search case ID, company, service, status..."
                  className="rounded-none pl-9"
                  data-testid="case-search-input"
                />
              </div>
              <div className="flex items-center gap-2 text-[#4B5563]">
                <Funnel className="w-5 h-5" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              
              <Select value={filters.service_type || 'all'} onValueChange={(v) => handleFilterChange('service_type', v)}>
                <SelectTrigger className="w-48 rounded-none" data-testid="filter-service-type">
                  <SelectValue placeholder="Service Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {SERVICE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.internal_status || 'all'} onValueChange={(v) => handleFilterChange('internal_status', v)}>
                <SelectTrigger className="w-48 rounded-none" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {INTERNAL_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.priority || 'all'} onValueChange={(v) => handleFilterChange('priority', v)}>
                <SelectTrigger className="w-36 rounded-none" data-testid="filter-priority">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="ml-auto flex items-center gap-3">
                <Button
                  type="button"
                  variant={showArchived ? 'default' : 'outline'}
                  onClick={() => { setLoading(true); setShowArchived((v) => !v); }}
                  className={`rounded-none ${showArchived ? 'bg-[#0A192F] hover:bg-[#0A192F]/90' : ''}`}
                  data-testid="toggle-archived-cases-btn"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  {showArchived ? 'Viewing Archived' : 'Show Archived'}
                </Button>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="create-case-btn">
                      <Plus className="w-4 h-4 mr-2" /> New Case
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle>Create New Case</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateCase} className="space-y-4 mt-4">
                      <div>
                        <Label className="text-sm font-semibold">Company *</Label>
                        <Select value={newCase.company_id} onValueChange={(v) => setNewCase({ ...newCase, company_id: v })}>
                          <SelectTrigger className="rounded-none mt-1" data-testid="case-company-select">
                            <SelectValue placeholder="Select company" />
                          </SelectTrigger>
                          <SelectContent>
                            {companies.map((c) => (
                              <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-sm font-semibold">Service Type *</Label>
                        <Select value={newCase.service_type} onValueChange={(v) => setNewCase({ ...newCase, service_type: v })}>
                          <SelectTrigger className="rounded-none mt-1" data-testid="case-service-select">
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent>
                            {SERVICE_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-semibold">Status</Label>
                          <Select value={newCase.internal_status} onValueChange={(v) => setNewCase({ ...newCase, internal_status: v })}>
                            <SelectTrigger className="rounded-none mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {INTERNAL_STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Priority</Label>
                          <Select value={newCase.priority} onValueChange={(v) => setNewCase({ ...newCase, priority: v })}>
                            <SelectTrigger className="rounded-none mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PRIORITIES.map((p) => (
                                <SelectItem key={p} value={p}>{p}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold">Internal Notes</Label>
                        <Textarea 
                          value={newCase.internal_notes}
                          onChange={(e) => setNewCase({ ...newCase, internal_notes: e.target.value })}
                          className="rounded-none mt-1"
                          rows={3}
                          data-testid="case-notes-input"
                        />
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="submit-case-btn">
                          Create Case
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cases Table */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : visibleCases.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-[#E5E7EB]">
                      <th className="text-left py-3 px-4">Case</th>
                      <th className="text-left py-3 px-4">Company</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Priority</th>
                      <th className="text-left py-3 px-4">Date Logged</th>
                      <th className="text-right py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCases.map((c) => (
                      <tr key={c.case_id} className="border-b border-[#E5E7EB] hover:bg-[#F8F9FA]" data-testid={`case-row-${c.case_id}`}>
                        <td className="py-3 px-4">
                          <Link to={`/admin/cases/${c.case_id}`} className="flex items-center gap-3 group">
                            <div className="w-10 h-10 bg-[#0055FF]/10 flex items-center justify-center">
                              {c.service_type?.includes('Water') ? (
                                <Drop className="w-5 h-5 text-[#0055FF]" />
                              ) : (
                                <Lightning className="w-5 h-5 text-[#0055FF]" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-[#0055FF] group-hover:underline">{c.service_type}</p>
                              <p className="text-xs text-[#4B5563] font-mono">{c.case_id}</p>
                            </div>
                          </Link>
                        </td>
                        <td className="py-3 px-4 text-[#4B5563]">{c.company_name || 'Unknown'}</td>
                        <td className="py-3 px-4">
                          <Badge className={STATUS_COLORS[c.internal_status] || 'bg-gray-100'}>
                            {c.internal_status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={PRIORITY_COLORS[c.priority] || 'bg-gray-100'}>
                            {c.priority}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-[#4B5563] text-sm">
                          {new Date(c.date_logged || c.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {c.archived_at ? (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={busyId === c.case_id}
                              onClick={() => handleRestore(c)}
                              className="rounded-none text-[#0055FF] border-[#0055FF]/40 hover:bg-[#0055FF]/5"
                              data-testid={`restore-case-btn-${c.case_id}`}
                            >
                              <ArrowCounterClockwise className="w-4 h-4 mr-1" /> Restore
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={busyId === c.case_id}
                              onClick={() => handleArchive(c)}
                              className="rounded-none text-red-600 border-red-300 hover:bg-red-50"
                              data-testid={`archive-case-btn-${c.case_id}`}
                            >
                              <Archive className="w-4 h-4 mr-1" /> Delete
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Folder className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
                <p className="text-[#4B5563]" data-testid="cases-empty-text">
                  {query ? `No cases match "${search.trim()}"` : showArchived ? 'No archived queries' : 'No cases found'}
                </p>
                {!showArchived && !query && (
                  <Button 
                    onClick={() => setIsDialogOpen(true)} 
                    className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] rounded-none"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Create First Case
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminCases;
